import React, { FC, useEffect, useRef, useState } from 'react';
import { Pause, Play, StopCircle } from 'react-feather';
import Typography from '../typography/Typography';

interface TicketTimer {
  startTime: string | Date;
  endTime?: string | Date;
  entryId: string;
  totalTime?: string;
  elapsed?: number;
  status?: string; // 'running', 'pause', 'closed'
  onStart?: (entryId: string, elapsedTime?: number, type?: string) => void;
  onStop?: (entryId: string, finalDurationMs: number) => void;
}

const statusColor = {
  pause: 'bg-orange-200',
  resumed: 'bg-green-200',
  closed: 'bg-red-200',
};

export const Timer: FC<TicketTimer> = ({
  startTime,
  endTime,
  entryId,
  totalTime,
  elapsed,
  onStart,
  onStop,
  status,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  const animationFrameId = useRef<number>();
  const baseTimeRef = useRef(0);
  const animationLoopStartTimeRef = useRef(0);

  const iconSize = '16';
  const isFinalized = status === 'closed';

  // Initialize timer based on status
  useEffect(() => {
    const getTimestamp = (dateInput?: string | Date): number => {
      if (!dateInput) return 0;
      const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
      const timestamp = date.getTime();
      return isNaN(timestamp) ? 0 : timestamp;
    };

    const initialStartTimeMs = getTimestamp(startTime);
    const initialEndTimeMs = getTimestamp(endTime);
    const currentTimeMs = Date.now();

    if (isFinalized) {
      const finalCalculatedTime = Math.max(
        0,
        initialEndTimeMs - initialStartTimeMs
      );
      setElapsedTime(finalCalculatedTime);
      baseTimeRef.current = finalCalculatedTime;
      setIsPlaying(false);
    } else if (status === 'pause') {
      const pausedElapsed = typeof elapsed === 'number' ? elapsed : 0;
      setElapsedTime(pausedElapsed);
      baseTimeRef.current = pausedElapsed;
      setIsPlaying(false);
    } else {
      let calculatedInitialElapsed = 0;
      if (typeof elapsed === 'number' && elapsed > 0) {
        calculatedInitialElapsed = elapsed;
      } else {
        calculatedInitialElapsed = Math.max(
          0,
          currentTimeMs - initialStartTimeMs
        );
      }
      setElapsedTime(calculatedInitialElapsed);
      baseTimeRef.current = calculatedInitialElapsed;
      setIsPlaying(true);
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = undefined;
      }
    };
  }, [startTime, endTime, status, elapsed, isFinalized, entryId]);

  // Animation loop to update elapsed time
  useEffect(() => {
    if (!isPlaying || isFinalized) return;

    animationLoopStartTimeRef.current = Date.now();

    const animate = () => {
      const now = Date.now();
      const timeSinceStart = now - animationLoopStartTimeRef.current;
      setElapsedTime(baseTimeRef.current + timeSinceStart);
      animationFrameId.current = requestAnimationFrame(animate);
    };

    animationFrameId.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = undefined;
      }
    };
  }, [isPlaying, isFinalized]);

  const handlePlay = () => {
    if (!isPlaying && !isFinalized) {
      const resumeFrom = typeof elapsed === 'number' ? elapsed : elapsedTime;
      baseTimeRef.current = resumeFrom;
      setElapsedTime(resumeFrom);
      animationLoopStartTimeRef.current = Date.now();
      setIsPlaying(true);
      if (onStart && entryId) {
        onStart(entryId, resumeFrom, 'resume');
      }
    }
  };

  const handlePause = () => {
    if (!isFinalized) {
      setIsPlaying(false);
      if (onStart && entryId) {
        onStart(entryId, elapsedTime);
      }
    }
  };

  const handleStop = () => {
    if (!isFinalized) {
      // setIsPlaying(false);
      if (onStop && entryId) {
        onStop(entryId, elapsedTime);
      }
      // setElapsedTime(0);
      // baseTimeRef.current = 0;
    }
  };

  const formatTime = (ms: number) => {
    const safeMs = Math.max(0, ms);
    const totalSeconds = Math.floor(safeMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const hours = Math.floor(minutes / 60);
    const displayMinutes = minutes % 60;

    return `${hours.toString().padStart(2, '0')}:${displayMinutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative">
      <div className="grid grid-cols-3 place-items-center rounded py-2 shadow-md">
        <button
          onClick={status === 'pause' ? handlePlay : handlePause}
          className="focus:outline-none"
          disabled={isFinalized}
        >
          {status === 'pause' || !isPlaying ? (
            <Play size={iconSize} />
          ) : (
            <Pause size={iconSize} />
          )}
        </button>

        <div>
          <Typography tag="span" type="label" noTranslation>
            {formatTime(elapsedTime)}
          </Typography>
          {totalTime}
        </div>

        <button
          onClick={handleStop}
          className="focus:outline-none"
          disabled={isFinalized}
        >
          <StopCircle size={iconSize} />
        </button>
      </div>

      <div className="w-full max-w-2xl bg-gray-100 rounded h-[2px] overflow-hidden shadow-inner flex">
        <div
          className={`h-full ${!status ? 'bg-green-200' : statusColor[status]} rounded-full transition-transform ease-linear w-full`}
        ></div>
      </div>
    </div>
  );
};
